import React, { useEffect, useState } from 'react';
import { api, apiErr } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { fmtDateTimeMX } from '@/lib/format';
import { Monitor, Smartphone, Tablet, Laptop, MapPin, LogOut, ShieldAlert } from 'lucide-react';

const deviceIcon = (t) => {
  const s = (t || '').toLowerCase();
  if (s.includes('iphone') || s.includes('android') || s.includes('móvil')) return Smartphone;
  if (s.includes('ipad') || s.includes('tablet')) return Tablet;
  if (s.includes('mac')) return Laptop;
  return Monitor;
};

export default function SessionsPanel() {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmAll, setConfirmAll] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/auth/sessions');
      setSessions(r.data || []);
    } catch (e) { toast.error(apiErr(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const revoke = async (sid, isCurrent) => {
    const msg = isCurrent ? '¿Cerrar esta sesión (la actual)? Serás redirigido al inicio de sesión.' : '¿Cerrar esta sesión?';
    if (!window.confirm(msg)) return;
    try {
      const r = await api.delete(`/auth/sessions/${sid}`);
      toast.success('Sesión cerrada');
      if (r.data?.was_current) {
        setTimeout(() => { window.location.href = '/'; }, 400);
        return;
      }
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const closeAll = async () => {
    try {
      const r = await api.post('/auth/sessions/close-all');
      toast.success(`Se cerraron ${r.data.revoked} sesión(es). La actual se conservó.`);
      setConfirmAll(false);
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <Card className="p-4 border-border rounded-xl" data-testid="sessions-panel">
      <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground mb-3">Sesiones activas</p>
      {loading ? (
        <p className="text-sm text-muted-foreground">Cargando…</p>
      ) : sessions.length === 0 ? (
        <p className="text-sm text-muted-foreground">Sin sesiones activas.</p>
      ) : (
        <div className="space-y-2" data-testid="sessions-list">
          {sessions.map(s => {
            const Icon = deviceIcon(s.device_type);
            return (
              <div key={s.id} data-testid={`session-${s.id}`} className={`rounded-lg border p-3 flex items-start gap-3 ${s.is_current ? 'border-primary/40 bg-primary/5' : 'border-border'}`}>
                <div className="h-9 w-9 rounded-lg bg-secondary/60 flex items-center justify-center flex-shrink-0">
                  <Icon className="h-4 w-4"/>
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-medium">{s.device_type} · {s.browser}</p>
                    {s.is_current && <span data-testid="session-current-badge" className="text-[10px] uppercase tracking-widest bg-primary/15 text-primary rounded px-1.5 py-0.5">Este dispositivo</span>}
                    {s.is_new_device && !s.is_current && <span className="text-[10px] uppercase tracking-widest bg-amber-500/15 text-amber-700 dark:text-amber-500 rounded px-1.5 py-0.5">Nuevo</span>}
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1"><MapPin className="h-3 w-3"/> {s.location_label || 'Ubicación no disponible'} · <span className="font-mono-num">{s.ip}</span></p>
                  <p className="text-[11px] text-muted-foreground mt-0.5">Inicio: {fmtDateTimeMX(s.created_at)}</p>
                  <p className="text-[11px] text-muted-foreground">Última actividad: {fmtDateTimeMX(s.last_activity)}</p>
                </div>
                <button data-testid={`session-revoke-${s.id}`} onClick={() => revoke(s.id, s.is_current)} className="p-2 rounded-full hover:bg-secondary text-destructive flex-shrink-0" title="Cerrar sesión">
                  <LogOut className="h-4 w-4"/>
                </button>
              </div>
            );
          })}
        </div>
      )}
      <Button data-testid="btn-close-all-sessions" variant="outline" onClick={() => setConfirmAll(true)} className="w-full mt-4 h-11 rounded-full border-destructive/30 text-destructive">
        <ShieldAlert className="h-4 w-4 mr-2"/> Cerrar todas las sesiones
      </Button>
      {confirmAll && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" data-testid="confirm-close-all-modal">
          <div className="bg-card rounded-xl border border-border p-6 max-w-md w-full space-y-4">
            <p className="font-display font-bold text-lg">Cerrar todas las sesiones</p>
            <p className="text-sm text-muted-foreground">¿Estás seguro de que deseas cerrar todas las sesiones? Se cerrará el acceso a todos los dispositivos. Tu sesión actual se conservará.</p>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setConfirmAll(false)} className="flex-1 h-11 rounded-full" data-testid="close-all-cancel">Cancelar</Button>
              <Button onClick={closeAll} className="flex-1 h-11 rounded-full" data-testid="close-all-confirm">Estoy de acuerdo</Button>
            </div>
          </div>
        </div>
      )}
    </Card>
  );
}
